/**
 * ABCDior order details: the order on the left, editable while pending, the
 * on-hold flag, product picture, status logs and additional notes on the right.
 */
import { useEffect, useRef, useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import type { Role } from '@/shared/constants';
import type { Order } from './api';
import { ORDER_TYPES, STATUS_COLORS } from './order-types';
import { formatOrderTime, textParts } from './logic';
import {
  canEditAdditionalNotes,
  checkDetailsForm,
  customTextWithinLength,
  detailsValues,
  findProductLine,
  isFieldRequired,
  isOrderEditable,
  isTruthyFlag,
  productPictureUrl,
  showsClientData,
  sortStatusLogs,
  storeModeLabel,
  toSavedOrder,
  type OrderDetailsValues,
} from './details-logic';
import { StatusLogs } from './StatusLogs';
import { dialogCancelHandler } from '@/shared/ui/dialog-cancel';

export interface OrderDetailsDialogProps {
  order: Order;
  role: Role;
  productLines: readonly Record<string, unknown>[] | undefined;
  onClose: () => void;
  /** Saves the order, reloading the lists when asked. */
  onSave: (order: Order, reload: boolean) => void;
}

const SECTION_TITLE = { fontSize: 30 } as const;

export function OrderDetailsDialog({
  order,
  role,
  productLines,
  onClose,
  onSave,
}: Readonly<OrderDetailsDialogProps>) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const line = findProductLine(productLines, order.productLine);
  const editable = isOrderEditable(role, order);
  const showClient = showsClientData(role);
  const logs = sortStatusLogs(order.statusLogs);
  const picture = productPictureUrl(order);

  const [values, setValues] = useState<OrderDetailsValues>(() =>
    detailsValues(order),
  );
  const [staged, setStaged] = useState(values);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // The form check runs when the dialog opens: a missing order id is reported at once.
  const [errorKey, setErrorKey] = useState<string | null>(
    () => checkDetailsForm(detailsValues(order), order, line).errorKey,
  );
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [initialNotes, setInitialNotes] = useState('');
  const [enlarged, setEnlarged] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  const set = <K extends keyof OrderDetailsValues>(
    key: K,
    value: OrderDetailsValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const enterEditMode = () => {
    setStaged(values);
    setIsEditMode(true);
  };

  const cancelEdit = () => {
    setValues(staged);
    setIsEditMode(false);
  };

  const save = (next: OrderDetailsValues, reload: boolean) => {
    onSave(toSavedOrder(order, next, line), reload);
    setStaged(next);
  };

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    const check = checkDetailsForm(values, order, line);
    setErrorKey(check.errorKey);
    if (check.fatal) return;
    save(values, true);
    setIsEditMode(false);
  };

  const onHoldChanged = (onHold: boolean) => {
    const next = { ...values, onHold };
    setValues(next);
    save(next, false);
  };

  const saveNotes = () => {
    save(values, false);
    setNotesEditMode(false);
  };

  const check = submitted ? checkDetailsForm(values, order, line) : null;
  const customTextError =
    !!check?.customTextInvalid && customTextWithinLength(values, order, line);
  const pencil = (label: string, testId?: string) => (
    <button
      type="button"
      className="pencil"
      aria-label={`Edit ${label}`}
      data-testid={testId}
      onClick={enterEditMode}
    >
      <FontAwesomeIcon icon={faPencilAlt} />
    </button>
  );
  const sizeEditable = editable && line.sizeDisplay !== 'shoeSize';
  const disabledClass = !isEditMode || !editable ? 'text-disabled' : '';

  return (
    <dialog
      ref={dialogRef}
      className="app-dialog order-details-dialog"
      aria-label={`CUSTOMIZATION - ${order.displayId ?? ''}`}
      data-testid="order-details-dialog"
      onCancel={dialogCancelHandler(onClose)}
      onClose={onClose}
    >
      <div
        className={`order-details ${isEditMode ? 'edit-mode' : 'view-mode'}`}
      >
        <div className="order-details-left px-6">
          <div className="details-modal-header mt-4 mb-2">
            CUSTOMIZATION - {order.displayId}
          </div>
          <form onSubmit={onSubmit} noValidate>
            <div className="mb-2">
              <div className="flex" data-testid="order-details-status">
                <span className="modal-label">{t('orders.status')}:</span>
                <span
                  style={{ color: STATUS_COLORS[order.customisationStatusId] }}
                >
                  {ORDER_TYPES[order.customisationStatusId]}
                </span>
                {values.onHold && (
                  <span className="text-danger ml-2">
                    (<FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                    {t('orders.onHold')})
                  </span>
                )}
              </div>
              <div className="flex" data-testid="order-details-date">
                <span className="modal-label">{t('orders.date')}:</span>
                <span>{formatOrderTime(order, 'MMMM D, YYYY')}</span>
              </div>
              <div className="flex" data-testid="order-details-purchase">
                <span className="modal-label">{t('orders.purchase')}:</span>
                <span>
                  {t(
                    isTruthyFlag(order.purchaseStatus)
                      ? 'orders.justPurchased'
                      : 'orders.alreadyOwned',
                  )}
                </span>
              </div>
              <div data-testid="order-details-text">
                <span className="modal-label">
                  {t('orders.text')}{' '}
                  {editable &&
                    pencil(t('orders.text'), 'order-details-text-edit-button')}
                  :
                </span>
                {textParts(order.customText).map((part, index) => (
                  <span
                    key={`${index}-${part.value}`}
                    className="view-item"
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: part.symbol
                        ? 'DiorSymbolesCondensedBold'
                        : undefined,
                    }}
                  >
                    {part.value}
                  </span>
                ))}
                <input
                  type="text"
                  className={`form-control edit-item ${customTextError ? 'error' : ''}`}
                  name="customText"
                  aria-label={t('orders.text')}
                  data-testid="order-details-text-input"
                  readOnly={!editable}
                  value={values.customText}
                  onChange={(event) => set('customText', event.target.value)}
                />
              </div>
              <div data-testid="order-details-alphabet">
                <span className="modal-label">{t('orders.alphabet')}:</span>
                <span>{order.alphabet}</span>
              </div>
              <div data-testid="order-details-color">
                <span className="modal-label">{t('orders.color')}:</span>
                <span>{order.color?.name || order.productColor}</span>
              </div>
              <div data-testid="order-details-priority">
                <span className="modal-label">{t('orders.priority')}:</span>
                <div className="styled-input-square inline-flex">
                  {(['true', 'false'] as const).map((choice) => (
                    <div
                      key={choice}
                      className="styled-input-single"
                      style={{ marginRight: choice === 'true' ? 10 : 0 }}
                    >
                      <input
                        type="radio"
                        id={`priority-${choice}-${order.id}`}
                        name="priority"
                        value={choice}
                        data-testid="order-details-priority-input"
                        data-value={choice}
                        checked={values.priority === choice}
                        disabled={!isEditMode || !editable}
                        onChange={() => set('priority', choice)}
                      />
                      <label
                        htmlFor={`priority-${choice}-${order.id}`}
                        className={`${choice === 'true' ? 'text-danger' : ''} ${disabledClass}`}
                        style={{ marginBottom: 0 }}
                      >
                        {t(choice === 'true' ? 'orders.high' : 'orders.normal')}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex" data-testid="order-details-transaction-id">
                <span className="modal-label">
                  {t('orders.orderId')}{' '}
                  {editable &&
                    pencil(
                      t('orders.orderId'),
                      'order-details-transaction-id-edit-button',
                    )}
                  :
                </span>
                <span className="view-item">{values.externalOrderId}</span>
                <input
                  type="text"
                  className={`form-control edit-item ${check?.orderIdInvalid ? 'error' : ''}`}
                  name="externalOrderId"
                  placeholder={t('orders.orderId')}
                  aria-label={t('orders.orderId')}
                  data-testid="order-details-transaction-id-input"
                  readOnly={!editable}
                  required={isFieldRequired(order, 'externalOrderId')}
                  value={values.externalOrderId}
                  onChange={(event) =>
                    set('externalOrderId', event.target.value)
                  }
                />
              </div>
              <div className="flex" data-testid="order-details-client-id">
                <span className="modal-label">
                  {t('orders.clientId')}{' '}
                  {showClient &&
                    editable &&
                    pencil(
                      t('orders.clientId'),
                      'order-details-client-id-edit-button',
                    )}
                  :
                </span>
                <span className="view-item">
                  {showClient ? values.externalClientId : 'XXX'}
                </span>
                {showClient && editable && (
                  <input
                    type="text"
                    className="form-control edit-item"
                    name="externalClientId"
                    placeholder={t('orders.clientId')}
                    aria-label={t('orders.clientId')}
                    data-testid="order-details-client-id-input"
                    value={values.externalClientId}
                    onChange={(event) =>
                      set('externalClientId', event.target.value)
                    }
                  />
                )}
              </div>
            </div>

            {showClient && (
              <div className="mb-2">
                <b style={SECTION_TITLE} className="mb-2">
                  {t('orders.client')}
                </b>
                {(['firstName', 'lastName'] as const).map((field) => {
                  const testId =
                    field === 'firstName'
                      ? 'order-details-client-first-name'
                      : 'order-details-client-last-name';
                  return (
                    <div className="flex" key={field} data-testid={testId}>
                      <span className="modal-label">
                        {t(`orders.${field}`)}{' '}
                        {editable &&
                          pencil(t(`orders.${field}`), `${testId}-edit-button`)}
                        :
                      </span>
                      <span className="view-item">{values[field]}</span>
                      <input
                        type="text"
                        className="form-control edit-item"
                        name={field}
                        aria-label={t(`orders.${field}`)}
                        data-testid={`${testId}-input`}
                        readOnly={!editable}
                        value={values[field]}
                        onChange={(event) => set(field, event.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mb-2">
              <b style={SECTION_TITLE} className="mb-2">
                {t('orders.product')}
              </b>
              <div data-testid="order-details-product-ref">
                <span className="modal-label">REF:</span>
                <span>{order.productReference}</span>
              </div>
              <div className="flex" data-testid="order-details-product-item">
                <span className="modal-label">{t('orders.item')}:</span>
                <span>{order.productName}</span>
              </div>
              {line.sizeDisplay !== 'noSize' && (
                <div data-testid="order-details-size">
                  <span className="modal-label">
                    {t('orders.size')}{' '}
                    {sizeEditable &&
                      pencil(
                        t('orders.size'),
                        'order-details-size-edit-button',
                      )}
                    :
                  </span>
                  <span className="view-item">{values.productSize}</span>
                  <input
                    type="text"
                    className="form-control edit-item"
                    name="productSize"
                    aria-label={t('orders.size')}
                    data-testid="order-details-size-input"
                    readOnly={!sizeEditable}
                    value={values.productSize}
                    onChange={(event) => set('productSize', event.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="mb-2">
              <b style={SECTION_TITLE} className="mb-2">
                {t('orders.store')}
              </b>
              <div className="flex" data-testid="order-details-store">
                <span className="modal-label">
                  {t('orders.storeCodeName')}:
                </span>
                <span>
                  {order.storeCode} - {order.storeName}
                </span>
              </div>
              <div className="flex" data-testid="order-details-sales-associate">
                <span className="modal-label">
                  {t('orders.salesAssociate')}:
                </span>
                <span>{order.sellerName}</span>
              </div>
              <div className="flex" data-testid="order-details-store-mode">
                <span className="modal-label">{t('orders.storeMode')}:</span>
                <span>{storeModeLabel(order.product?.storeMode)}</span>
              </div>
            </div>

            <div className="mb-2">
              <b style={SECTION_TITLE} className="mb-2">
                {t('orders.workshop')}
              </b>
              <div className="flex" data-testid="order-details-workshop">
                <span className="modal-label">{t('orders.workshop')}:</span>
                <span>
                  {order.workshopName}{' '}
                  {order.displayId?.startsWith('C') ? '(Central)' : ''}
                </span>
              </div>
              <div className="flex" data-testid="order-details-oa">
                <span className="modal-label">OA:</span>
                <span>{order.oaCode}</span>
              </div>
              {order.receivedDateBlois && (
                <div
                  className="flex"
                  data-testid="order-details-received-date-blois"
                >
                  <span className="modal-label">
                    {t('selectedOrder.receivedDateBlois')}
                  </span>
                  <span>
                    {formatOrderTime(
                      order,
                      'MMMM D, YYYY',
                      'receivedDateBlois',
                    )}
                  </span>
                </div>
              )}
              {order.shipmentDateBlois && (
                <div
                  className="flex"
                  data-testid="order-details-shipment-date-blois"
                >
                  <span className="modal-label">
                    {t('selectedOrder.shipmentDateBlois')}
                  </span>
                  <span>
                    {formatOrderTime(
                      order,
                      'MMMM D, YYYY',
                      'shipmentDateBlois',
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-2">
              {errorKey && (
                <div
                  className="alert-danger text-center"
                  role="alert"
                  data-testid="order-details-error"
                >
                  {t(errorKey)}
                </div>
              )}
              {isEditMode ? (
                <button
                  key="save"
                  type="submit"
                  className="btn btn-dark modal-button"
                  data-testid="order-details-save-button"
                >
                  {t('orders.save')}
                </button>
              ) : (
                editable && (
                  <button
                    key="edit"
                    type="button"
                    className="btn btn-dark modal-button"
                    data-testid="order-details-edit-button"
                    onClick={enterEditMode}
                  >
                    {t('orders.edit')}
                  </button>
                )
              )}
            </div>
            {isEditMode && (
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-dark modal-button"
                  data-testid="order-details-cancel-button"
                  onClick={cancelEdit}
                >
                  {t('orders.cancelOrder')}
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="order-details-right">
          <button
            type="button"
            className="close p-2"
            aria-label="Close"
            data-testid="order-details-close-button"
            onClick={onClose}
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="onhold-container">
            <div className="styled-input-square inline-flex">
              <div className="styled-input-single" style={{ marginRight: 10 }}>
                <input
                  type="checkbox"
                  id={`onhold-${order.id}`}
                  name="onHold"
                  data-testid="order-details-hold-toggle"
                  checked={values.onHold}
                  onChange={(event) => onHoldChanged(event.target.checked)}
                />
                <label htmlFor={`onhold-${order.id}`}>
                  {t('orders.personalisationOnHold')}
                </label>
                <div>{t('orders.seeAdditionalNotes')}</div>
              </div>
            </div>
          </div>
          {picture && (
            <button
              type="button"
              className="product-pic-preview"
              aria-label="Enlarge the product picture"
              data-testid="order-details-picture-button"
              onClick={() => setEnlarged(picture)}
            >
              <img alt="" src={picture} data-testid="order-details-picture" />
            </button>
          )}

          <div className="order-details-side">
            <div className="status-logs">
              <StatusLogs logs={logs} />
            </div>
            <div className="pr-4">
              <label className="modal-label" htmlFor={`notes-${order.id}`}>
                {t('orders.additionalNotes')}
              </label>
              <br />
              <textarea
                id={`notes-${order.id}`}
                cols={30}
                rows={7}
                className="form-control modal-textarea"
                data-testid="order-details-notes-textbox"
                value={values.notes}
                disabled={!notesEditMode}
                onChange={(event) => set('notes', event.target.value)}
              />
              {notesEditMode ? (
                <>
                  <button
                    key="notes-save"
                    type="button"
                    className="btn btn-dark modal-button"
                    data-testid="order-details-notes-save-button"
                    onClick={saveNotes}
                  >
                    {t('orders.save')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-dark modal-button mt-2 mb-6"
                    data-testid="order-details-notes-cancel-button"
                    onClick={() => {
                      set('notes', initialNotes);
                      setNotesEditMode(false);
                    }}
                  >
                    {t('orders.cancelOrder')}
                  </button>
                </>
              ) : (
                canEditAdditionalNotes(role, isEditMode) && (
                  <button
                    key="notes-edit"
                    type="button"
                    className="btn btn-dark modal-button mb-6"
                    data-testid="order-details-notes-edit-button"
                    onClick={() => {
                      setInitialNotes(values.notes);
                      setNotesEditMode(true);
                    }}
                  >
                    {t('orders.edit')}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      {enlarged && (
        <EnlargedImageDialog src={enlarged} onClose={() => setEnlarged(null)} />
      )}
    </dialog>
  );
}

function EnlargedImageDialog({
  src,
  onClose,
}: Readonly<{ src: string; onClose: () => void }>) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="app-dialog image-dialog"
      aria-label="Product picture"
      data-testid="order-details-picture-dialog"
      onCancel={dialogCancelHandler(onClose)}
      onClose={onClose}
    >
      <div className="image-dialog-content">
        <button
          type="button"
          className="close p-2"
          aria-label="Close"
          data-testid="order-details-picture-dialog-close-button"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <img
          src={src}
          alt="Product"
          className="enlarged-img"
          data-testid="order-details-picture-dialog-image"
        />
      </div>
    </dialog>
  );
}
